/**
 * The app's one map (spec 2026-09-17 §4, I1): every screen, tab and thing a
 * person can be taken to, declared once, here. Everything else is derived:
 * control_app's enum and description, the nav reflex's names, channel.show's
 * kinds, the situation line's view words, the web's view stamp, the sidebar
 * and the tabs, the desktop's route belt (scripts/gen-app-routes.mjs), the
 * evals' corpus. A page cannot exist without this table knowing it and a
 * destination cannot exist without a page: apps/web's destinations-pages
 * test walks the file system both ways.
 *
 * Types and pure helpers only: no React, no server import, nothing at module
 * scope but the table, one regex and the `pathOf` arrow. A destination is
 * DATA (I2): the model picks an id from a closed list; the route is computed
 * from the destination and a resolved id. A redirect source in next.config.ts
 * is never a route here (spec §2.5): a spoken habit for a retired route
 * ("wiki", "memory") lives on as a synonym of the page it lands on.
 *
 * Phrases obey the catalog's rules (catalog/lints.ts LEADS and density): verb
 * first, three to eight, English and Hinglish. Every SCREEN phrase is an
 * exact hit of the nav reflex (server nav-fastpath.ts) for its own screen and
 * for no other, so the Hinglish ones take the "mujhe <name> pe le chalo"
 * shape (R-P-3). A THING's phrases are what a person says to open one by
 * name; the reflex never matches them (it knows screens alone), and nothing
 * indexes them yet: they are the words a later card would be minted from.
 */
export type DestinationKind = 'screen' | 'thing';
export type Takes =
  | 'taskId' | 'routineId' | 'fileId' | 'creationId' | 'pagePath' | 'conversationId' | 'invoiceId'
  | 'appSlug' | 'skillSlug' | 'agentSlug' | 'abilitySlug' | 'prepPackId' | 'projectId';

export interface Destination {
  /** 'routines', 'library.skills', 'account.billing', 'task', 'routine', 'brain.page' */
  id: string;
  kind: DestinationKind;
  /** The screen's own word, as the sidebar and the tabs show it. */
  label: string;
  /** '/routines', '/library/skills', '/routines/:id', '/knowledge/files?file=:id' */
  route: string;
  /** The screen a tab or a thing belongs to: 'library.abilities' for 'library.skills', 'routines' for 'routine'. */
  parent?: string;
  /** What the route needs. Absent for a screen. */
  takes?: Takes;
  /** Three to eight things people say to go there, English and Hinglish, verb first (the card rules). */
  phrases: readonly string[];
  /** The other names a person has for it: 'vault' and 'documents' on files, 'wiki' on the Brain. */
  synonyms: readonly string[];
  /** Web only: the desktop belt leaves it out (the download page). */
  webOnly?: boolean;
}

const TABLE = [
  // ── Screens ────────────────────────────────────────────────────────────────
  { id: 'home', kind: 'screen', label: 'Home', route: '/home',
    phrases: ['open home', 'go to the home page', 'take me to my work', 'show me the dashboard', 'mujhe home pe le chalo', 'mujhe dashboard par le jao'],
    synonyms: ['my work', 'the start', 'dashboard'] },
  { id: 'task.new', kind: 'screen', label: 'New task', route: '/task', parent: 'home',
    phrases: ['open the new task page', 'mujhe new task pe le chalo', 'mujhe fresh task par le jao'],
    synonyms: ['a blank task', 'fresh task'] },
  { id: 'routines', kind: 'screen', label: 'Routines', route: '/routines',
    phrases: ['open my routines', 'go to routines', 'show me the routines page', 'take me to my automations', 'mujhe routines pe le chalo', 'mujhe schedules par le jao'],
    synonyms: ['automations', 'schedules', 'recurring work'] },
  { id: 'library.abilities', kind: 'screen', label: 'Abilities', route: '/library/abilities',
    phrases: ['open abilities', 'go to the abilities page', 'take me to the library', 'show me what bash can do', 'mujhe abilities pe le chalo', 'mujhe abilities par le jao'],
    synonyms: ['the library', 'what Bash can do'] },
  { id: 'library.skills', kind: 'screen', label: 'Skills', route: '/library/skills', parent: 'library.abilities',
    phrases: ['open the skills tab', 'go to my skills', 'mujhe skills pe le chalo'],
    synonyms: ['the skills tab', 'installed skills'] },
  { id: 'library.agents', kind: 'screen', label: 'Agents', route: '/library/agents', parent: 'library.abilities',
    phrases: ['open the agents tab', 'go to my agents', 'show me my teammates', 'mujhe agents pe le chalo', 'mujhe teammates par le jao'],
    synonyms: ['the agents tab', 'my agents', 'teammates'] },
  { id: 'library.mcps', kind: 'screen', label: 'MCPs', route: '/library/mcps', parent: 'library.abilities',
    phrases: ['open the mcps tab', 'go to my mcp servers', 'show me the connections tab', 'mujhe mcps pe le chalo', 'mujhe mcp servers par le jao'],
    synonyms: ['the connections tab', 'mcp servers'] },
  { id: 'knowledge.files', kind: 'screen', label: 'My files', route: '/knowledge/files',
    phrases: ['open my files', 'go to the vault', 'show me my documents', 'take me to my uploads', 'mujhe files pe le chalo', 'mujhe documents par le jao'],
    synonyms: ['files', 'the vault', 'documents', 'uploads'] },
  // The Media folder inside My files (the Finder-root redesign): a section, so it lands on the folder open, never on the files root.
  { id: 'knowledge.media', kind: 'screen', label: 'Media', route: '/knowledge/files?open=media', parent: 'knowledge.files',
    phrases: ['open media', 'show me the media library', 'go to my creations', 'mujhe creations pe le chalo', 'mujhe creations par le jao'],
    synonyms: ['the media library', 'media library', 'my media', 'creations'] },
  // Knowledge's third tab (Projects P1): the code folders on the box. The tab shows only when there is one.
  { id: 'knowledge.projects', kind: 'screen', label: 'Projects', route: '/knowledge/projects',
    phrases: ['open my projects', 'show me my code', 'go to my repos', 'mujhe projects pe le chalo', 'mujhe code projects par le jao'],
    synonyms: ['code projects', 'my repos', 'my code', 'repositories'] },
  { id: 'knowledge.brain', kind: 'screen', label: 'Brain', route: '/knowledge/brain',
    phrases: ['open my brain', 'go to the wiki', 'open memory', 'mujhe brain pe le chalo', 'mujhe memory par le jao'],
    synonyms: ['the wiki', 'memory', 'what Bash knows'] },
  { id: 'brain.you', kind: 'screen', label: 'You', route: '/knowledge/brain?view=you', parent: 'knowledge.brain',
    phrases: ['open my page', 'go to the you page', 'show me about me', 'mujhe about me pe le chalo'],
    synonyms: ['my page', 'about me', 'the you page'] },
  { id: 'brain.week', kind: 'screen', label: 'This week', route: '/knowledge/brain?view=week', parent: 'knowledge.brain',
    phrases: ['open this week', 'show me the week page', 'mujhe this week pe le chalo'],
    synonyms: ['the week page', 'what happened this week'] },
  { id: 'brain.waiting', kind: 'screen', label: 'Waiting', route: '/knowledge/brain?view=waiting', parent: 'knowledge.brain',
    phrases: ['open waiting', 'show me what is waiting', 'go to my open loops', 'mujhe waiting pe le chalo'],
    synonyms: ['what is waiting', 'open loops'] },
  // R-P-1: "receipts" is Billing's; the spec's table carried it here too.
  { id: 'brain.proof', kind: 'screen', label: 'Proof', route: '/knowledge/brain?view=proof', parent: 'knowledge.brain',
    phrases: ['open proof', 'show me the proof page', 'go to the evidence', 'mujhe proof pe le chalo'],
    synonyms: ['evidence'] },
  { id: 'apps', kind: 'screen', label: 'Connectors', route: '/apps',
    phrases: ['open connectors', 'take me to my connected apps', 'go to integrations', 'show me my apps', 'mujhe connectors pe le chalo', 'mujhe integrations par le jao'],
    synonyms: ['connected apps', 'integrations', 'connections', 'my apps'] },
  { id: 'support', kind: 'screen', label: 'Support', route: '/support',
    phrases: ['go to support', 'open the support page', 'take me to help', 'show me the front desk', 'mujhe support pe le chalo', 'mujhe feedback par le jao'],
    synonyms: ['help', 'feedback', 'the front desk', 'the Vestra team'] },
  { id: 'account.profile', kind: 'screen', label: 'Profile', route: '/account/profile',
    phrases: ['open my profile', 'go to my account', 'show me my passport', 'mujhe profile pe le chalo', 'mujhe passport par le jao'],
    synonyms: ['my account', 'my profile', 'passport'] },
  { id: 'account.settings', kind: 'screen', label: 'Settings', route: '/account/settings',
    phrases: ['open settings', 'go to my settings', 'take me to preferences', 'show me my spend limits', 'mujhe settings pe le chalo', 'mujhe controls par le jao'],
    synonyms: ['controls', 'preferences', 'spend limits'] },
  { id: 'account.plan', kind: 'screen', label: 'Plan', route: '/account/plan',
    phrases: ['open my plan', 'go to the plan page', 'show me my subscription', 'take me to pricing', 'mujhe plan pe le chalo', 'mujhe membership par le jao'],
    synonyms: ['membership', 'subscription', 'upgrade', 'pricing'] },
  { id: 'account.credits', kind: 'screen', label: 'Credits', route: '/account/credits',
    phrases: ['open credits', 'go to the credits page', 'show me my usage', 'take me to my balance', 'mujhe credits pe le chalo', 'mujhe balance par le jao'],
    synonyms: ['spend', 'usage', 'balance', 'the ledger'] },
  { id: 'account.billing', kind: 'screen', label: 'Billing', route: '/account/billing',
    phrases: ['take me to billing', 'open my invoices', 'go to the billing page', 'show me my payments', 'mujhe billing pe le chalo', 'mujhe receipts par le jao'],
    synonyms: ['invoices', 'receipts', 'payments', 'charges'] },
  { id: 'account.invites', kind: 'screen', label: 'Invites', route: '/account/invites',
    phrases: ['open invites', 'go to my invite codes', 'show me my referrals', 'mujhe invites pe le chalo', 'mujhe tickets par le jao'],
    synonyms: ['invite codes', 'referrals', 'tickets'] },
  // Bash's phone (spec 2026-10-14 §6.2). The label is "Phone", not "Bash's phone": a label's words may
  // be said by no other destination's phrase, and library.abilities says "what bash can do".
  { id: 'account.phone', kind: 'screen', label: 'Phone', route: '/account/phone',
    phrases: ["open bash's phone", 'go to the phone page', "take me to bash's number", 'show me his voice', 'mujhe phone pe le chalo', "mujhe bash's phone par le jao"],
    synonyms: ["bash's phone", "bash's number", 'his voice', 'my pin'] },
  // Contacts, Bash's phonebook (phone errands PR 2): served beside the phone page while the errands are on
  // (served-screens.ts). "Contacts" is Apple's app too: as with Home, no reflex launches it (launch-guard.ts
  // launchNamesAScreen), so "open contacts" is the nav row's, and the mind weighs it.
  { id: 'account.contacts', kind: 'screen', label: 'Contacts', route: '/account/contacts',
    phrases: ['open my contacts', 'go to the contacts page', "show me bash's phonebook", 'take me to my saved numbers', 'mujhe contacts pe le chalo', 'mujhe phonebook par le jao'],
    synonyms: ["bash's phonebook", 'phonebook', 'saved numbers'] },
  { id: 'agents.new', kind: 'screen', label: 'New agent', route: '/agents/new', parent: 'library.agents',
    phrases: ['open the agent editor', 'go to the new agent page', 'mujhe new agent pe le chalo'],
    synonyms: ['create an agent', 'the agent editor'] },
  { id: 'skills.new', kind: 'screen', label: 'New skill', route: '/skills/new', parent: 'library.skills',
    phrases: ['open the skill editor', 'go to the new skill page', 'mujhe new skill pe le chalo'],
    synonyms: ['create a skill', 'the skill editor'] },
  { id: 'download', kind: 'screen', label: 'Get Vestra for Mac', route: '/download', webOnly: true,
    phrases: ['open get vestra for mac', 'go to get vestra for mac', 'mujhe get vestra for mac pe le chalo'],
    synonyms: ['the desktop app', 'the mac app'] },
  // ── Things: the route carries an id the server resolved by owner (I3) ──────
  { id: 'task', kind: 'thing', label: 'Task', route: '/task/:id', takes: 'taskId', parent: 'home',
    phrases: ['open that task', 'take me to the task I ran', 'dikhao mera task'], synonyms: [] },
  { id: 'routine', kind: 'thing', label: 'Routine', route: '/routines/:id', takes: 'routineId', parent: 'routines',
    phrases: ['open the payroll routine', 'take me to that routine', 'kholo meri routine'], synonyms: [] },
  { id: 'routine.edit', kind: 'thing', label: 'Edit a routine', route: '/routines?edit=:id', takes: 'routineId', parent: 'routines',
    phrases: ['edit the payroll routine', 'change that routine', 'badal do meri routine'], synonyms: [] },
  { id: 'file', kind: 'thing', label: 'File', route: '/knowledge/files?file=:id', takes: 'fileId', parent: 'knowledge.files',
    phrases: ['open the offer letter file', 'show me that file', 'kholo meri file'], synonyms: [] },
  { id: 'creation', kind: 'thing', label: 'Creation', route: '/knowledge/files?open=media&creation=:id', takes: 'creationId', parent: 'knowledge.files',
    phrases: ['open the hero image creation', 'show me that creation', 'kholo meri creation'], synonyms: [] },
  // ?wiki= stays a legacy alias the Brain rewrites onto ?path= (brainDoor.ts wikiSlugFromSearch).
  { id: 'brain.page', kind: 'thing', label: 'Brain page', route: '/knowledge/brain?path=:path', takes: 'pagePath', parent: 'knowledge.brain',
    phrases: ['open the pricing page in my brain', 'show me that brain page', 'kholo mera brain page'], synonyms: [] },
  { id: 'support.request', kind: 'thing', label: 'Support request', route: '/support?c=:id', takes: 'conversationId', parent: 'support',
    phrases: ['open my support request', 'show me that support thread', 'kholo meri support request'], synonyms: [] },
  { id: 'invoice', kind: 'thing', label: 'Invoice', route: '/account/billing/invoice/:id', takes: 'invoiceId', parent: 'account.billing',
    phrases: ['open the march invoice', 'show me that invoice', 'kholo meri invoice'], synonyms: [] },
  { id: 'app', kind: 'thing', label: 'App', route: '/apps?app=:slug', takes: 'appSlug', parent: 'apps',
    phrases: ['open the gmail connector', 'show me the notion app', 'kholo mera gmail wala app'], synonyms: [] },
  { id: 'skill', kind: 'thing', label: 'Skill', route: '/skills/:slug', takes: 'skillSlug', parent: 'library.skills',
    phrases: ['open the deep research skill', 'show me that skill', 'kholo meri skill'], synonyms: [] },
  { id: 'agent', kind: 'thing', label: 'Agent', route: '/agents/:slug', takes: 'agentSlug', parent: 'library.agents',
    phrases: ['open the sales agent', 'show me that agent', 'kholo mera agent'], synonyms: [] },
  { id: 'ability', kind: 'thing', label: 'Ability', route: '/abilities/:slug', takes: 'abilitySlug', parent: 'library.abilities',
    phrases: ['open the studio ability', 'show me that ability', 'kholo meri ability'], synonyms: [] },
  { id: 'prep', kind: 'thing', label: 'Meeting prep', route: '/prep/:id', takes: 'prepPackId', parent: 'home',
    phrases: ['open the prep for my next meeting', 'show me my meeting prep', 'kholo meri meeting prep'], synonyms: [] },
  // Projects P2: one code folder on the box, its own page under the Projects tab.
  { id: 'project', kind: 'thing', label: 'Project', route: '/knowledge/projects/:id', takes: 'projectId', parent: 'knowledge.projects',
    phrases: ['open the shop project', 'show me that project', 'kholo mera project'], synonyms: [] },
] as const satisfies readonly Destination[];

/**
 * The table, widened to the interface. TABLE keeps its literal types so
 * DestinationId is the closed union of the ids the model may pick; DESTINATIONS
 * is what every reader walks, and a walker reads `parent`, `takes` and `webOnly`
 * on a row without knowing which row it holds, and a 38-member union of literal
 * object types carries no property that only some of its members declare.
 */
export const DESTINATIONS: readonly Destination[] = TABLE;

export type DestinationId = (typeof TABLE)[number]['id'];

const PARAM = /:(id|slug|path)\b/;
const pathOf = (route: string): string => route.split('?')[0]!.split('#')[0]!;

export function destinationById(id: string): Destination | undefined {
  return DESTINATIONS.find((d) => d.id === id);
}

export function isDestinationId(id: string): id is DestinationId {
  return DESTINATIONS.some((d) => d.id === id);
}

export function screens(): readonly Destination[] {
  return DESTINATIONS.filter((d) => d.kind === 'screen');
}

export function things(): readonly Destination[] {
  return DESTINATIONS.filter((d) => d.kind === 'thing');
}

/** The concrete path for a destination, the id encoded into its parameter; throws on a thing with no id. */
export function routeFor(id: DestinationId, thingId?: string): string {
  const d = destinationById(id);
  if (!d) throw new Error(`unknown destination ${id}`);
  if (!PARAM.test(d.route)) return d.route;
  if (!thingId) throw new Error(`destination ${id} takes ${d.takes ?? 'an id'}`);
  return d.route.replace(PARAM, encodeURIComponent(thingId));
}

/**
 * The destination a pathname is on, or null: the view stamp. Candidates rank on
 * the tuple [pattern segments, static segments, query-free, screen], compared
 * LEXICOGRAPHICALLY: the longest pattern wins (a pattern is a PREFIX: the task
 * page is an optional catch-all), then static segments beat parameters
 * (/skills/new is the editor, not a skill), then a query-free route beats a
 * section (the query never decides), then a screen beats a thing, and an exact
 * tie keeps the row the registry declared first (R-P-13). Lexicographic, not a
 * weighted sum: a sum lets a lower-ranked field outvote a higher one once the
 * table grows a route deep enough to overflow the weights.
 */
export function destinationOfPath(pathname: string): Destination | null {
  const segs = pathOf(pathname).split('/').filter(Boolean);
  let best: { d: Destination; rank: readonly number[] } | null = null;
  for (const d of DESTINATIONS) {
    const pat = pathOf(d.route).split('/').filter(Boolean);
    if (pat.length === 0 || pat.length > segs.length) continue;
    let statics = 0;
    let ok = true;
    for (let i = 0; i < pat.length; i += 1) {
      const p = pat[i]!;
      if (p.startsWith(':')) continue;
      if (p === segs[i]) statics += 1;
      else { ok = false; break; }
    }
    if (!ok) continue;
    const rank = [pat.length, statics, d.route.includes('?') ? 0 : 1, d.kind === 'screen' ? 1 : 0];
    if (!best) {
      best = { d, rank };
      continue;
    }
    const held = best.rank;
    const first = rank.findIndex((v, i) => v !== held[i]);
    if (first >= 0 && rank[first]! > held[first]!) best = { d, rank };
  }
  return best?.d ?? null;
}

/** Route prefixes for the desktop belt: every non-web-only destination's first segment, once, /task/ as the desktop writes it. */
export function routePrefixes(): readonly string[] {
  const out: string[] = [];
  for (const d of DESTINATIONS) {
    if (d.webOnly) continue;
    const first = `/${pathOf(d.route).split('/').filter(Boolean)[0]}`;
    const prefix = first === '/task' ? '/task/' : first;
    if (!out.includes(prefix)) out.push(prefix);
  }
  return out;
}
